/**
 * Property API Client
 * Uses standardized ApiResponse<T> wrapper for all endpoints
 */

import { apiGet, apiPost, apiPut, apiDelete, apiGetWithFallback, ApiError } from './api-response';
import { Property } from '../types/property';
import apiClient from '@/services/api-client';
import { logger } from '@/services/logger.service';

export const propertyAPI = {
  // Check server health
  async checkHealth(): Promise<boolean> {
    try {
      await apiClient.get('/system/health', { timeout: 5000 });
      return true;
    } catch (error) {
      logger.warn('Server health check failed - using fallback mode', { error });
      return false;
    }
  },

  // Fetch properties with server-side pagination
  async getProperties(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: 'newest' | 'oldest' | 'price_asc' | 'price_desc' | 'bedrooms_desc' | 'bathrooms_desc';
    source?: 'property24' | 'seller' | 'manual';
    featured?: boolean;
    unassigned?: boolean;
    minBedrooms?: number;
    minBathrooms?: number;
    priceMin?: number;
    priceMax?: number;
    propertyType?: string;
    listingStatus?: string;
  }): Promise<{
    listings: Property[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }> {
    try {
      const { page = 1, pageSize = 30, search, sortBy = 'newest', source, featured, unassigned, minBedrooms, minBathrooms, priceMin, priceMax, propertyType, listingStatus } = options || {};

      // Build query string
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      params.set('sortBy', sortBy);
      if (search) params.set('search', search);
      if (source) params.set('source', source);
      if (featured) params.set('featured', 'true');
      if (unassigned) params.set('unassigned', 'true');
      if (minBedrooms) params.set('minBedrooms', String(minBedrooms));
      if (minBathrooms) params.set('minBathrooms', String(minBathrooms));
      if (priceMin) params.set('priceMin', String(priceMin));
      if (priceMax && priceMax < 5000000) params.set('priceMax', String(priceMax));
      if (propertyType) params.set('propertyType', propertyType);
      if (listingStatus) params.set('listingStatus', listingStatus);

      console.log(`[PropertyAPI.getProperties] Fetching page ${page}, pageSize=${pageSize}, search=${search || 'none'}`);
      const response = await apiClient.get<any>(`/listings?${params.toString()}`);

      // Extract data from response
      const data = response?.data || response;
      const listings = data?.listings || [];
      const pagination = data?.pagination || {
        page: 1,
        pageSize: pageSize,
        totalItems: listings.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      };

      console.log(`[PropertyAPI.getProperties] Page ${pagination.page}/${pagination.totalPages}: ${listings.length} items, total: ${pagination.totalItems}`);

      return { listings, pagination };

    } catch (error: any) {
      logger.warn('getProperties: API request failed', { error: error.message });
      return {
        listings: [],
        pagination: {
          page: 1,
          pageSize: 30,
          totalItems: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false
        }
      };
    }
  },

  // Helper to extract listings array and pagination key from various response formats
  extractListingsFromResponse(response: any): { listings: Property[]; nextKey: string | null } {
    let listings: Property[] = [];
    let nextKey: string | null = null;

    // Case 1: Direct array response (no pagination info)
    if (Array.isArray(response)) {
      return { listings: response, nextKey: null };
    }

    // Case 2: Wrapped response { success: true, data: { listings: [...], lastEvaluatedKey } }
    if (response?.success === true && response?.data) {
      const data = response.data;
      if (Array.isArray(data)) {
        listings = data;
      } else if (data?.listings && Array.isArray(data.listings)) {
        listings = data.listings;
        // Extract pagination key - could be object or string
        if (data.lastEvaluatedKey) {
          nextKey = typeof data.lastEvaluatedKey === 'string'
            ? data.lastEvaluatedKey
            : JSON.stringify(data.lastEvaluatedKey);
        }
      }
      return { listings, nextKey };
    }

    // Case 3: Unwrapped response { listings: [...], lastEvaluatedKey }
    if (response?.listings && Array.isArray(response.listings)) {
      listings = response.listings;
      if (response.lastEvaluatedKey) {
        nextKey = typeof response.lastEvaluatedKey === 'string'
          ? response.lastEvaluatedKey
          : JSON.stringify(response.lastEvaluatedKey);
      }
      return { listings, nextKey };
    }

    // Case 4: Unknown format
    console.error('[PropertyAPI] Unknown response format:', response);
    return { listings: [], nextKey: null };
  },

  // Get a single property
  async getProperty(id: string): Promise<Property | null> {
    try {
      return await apiGet<Property>(`/listings/${id}`);
    } catch (error: any) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null; // Property not found
      }
      logger.error('Error getting property', { id, error: error.message });
      return null;
    }
  },

  // Create a new property
  async createProperty(propertyData: Omit<Property, 'id'>): Promise<Property> {
    try {
      return await apiPost<Property>('/listings', propertyData);
    } catch (error: any) {
      logger.error('Error creating property', { error: error.message });
      throw error;
    }
  },

  // Update a property
  async updateProperty(id: string, propertyData: Partial<Property>): Promise<Property> {
    try {
      return await apiPut<Property>(`/listings/${id}`, propertyData);
    } catch (error: any) {
      logger.error('Error updating property', { id, error: error.message });
      throw error;
    }
  },

  // Delete a property
  async deleteProperty(id: string): Promise<void> {
    try {
      await apiDelete<void>(`/listings/${id}`);
    } catch (error: any) {
      logger.error('Error deleting property', { id, error: error.message });
      throw error;
    }
  },

  // Archive a property (soft delete)
  async archiveProperty(id: string): Promise<Property> {
    try {
      return await apiDelete<Property>(`/listings/${id}`);
    } catch (error: any) {
      logger.error('Error archiving property', { id, error: error.message });
      throw error;
    }
  },

  // Assign agent to a property (with full denormalised agent display fields)
  async assignAgent(propertyId: string, agent: {
    agentId: string;
    agentName?: string;
    agentEmail?: string;
    agentPhone?: string;
    agentPhotoUrl?: string;
  }): Promise<any> {
    try {
      return await apiPut<any>(`/listings/${propertyId}/assign-agent`, agent);
    } catch (error: any) {
      logger.error('Error assigning agent to property', { propertyId, error: error.message });
      throw error;
    }
  },

  // Toggle the featured flag on a listing
  async featureListing(propertyId: string, featured: boolean): Promise<{ listingId: string; featured: boolean }> {
    try {
      return await apiPut<{ listingId: string; featured: boolean }>(`/listings/${propertyId}/feature`, { featured });
    } catch (error: any) {
      logger.error('Error updating featured flag', { propertyId, featured, error: error.message });
      throw error;
    }
  },

  // Seed properties (for initial data migration)
  // NOTE: This endpoint is currently disabled as /seed-properties doesn't exist in the API
  // If you need to seed properties, use the seller-properties/seed endpoint instead
  async seedProperties(properties: Property[]): Promise<{ count: number; newCount?: number; updatedCount?: number; agentAutoMapped?: number }> {
    logger.warn('seedProperties is currently disabled - endpoint does not exist');
    return { count: 0, newCount: 0, updatedCount: 0, agentAutoMapped: 0 };
  },

  // Delete ALL properties (bulk clear)
  async clearAllProperties(): Promise<{ deleted: number }> {
    try {
      logger.info('Clearing all properties from database...');
      const result = await apiDelete<{ deleted: number }>('/properties');
      logger.info(`Cleared ${result.deleted} properties`);
      return result;
    } catch (error: any) {
      logger.error('Error clearing properties', { error: error.message });
      throw error;
    }
  },

  // ─── Deduplication ───

  // Scan for duplicate properties
  async scanDuplicates(): Promise<{
    totalProperties: number;
    duplicateGroups: number;
    byExternalId: Array<{ externalId: string; count: number; ids: string[]; titles: string[] }>;
    byFingerprint: Array<{ fingerprint: string; count: number; ids: string[]; titles: string[] }>;
  }> {
    try {
      return await apiGet('/listings/dedup-scan');
    } catch (error: any) {
      logger.error('Error scanning duplicates', { error: error.message });
      throw error;
    }
  },

  // Auto-clean duplicate properties (merge & remove)
  async cleanDuplicates(): Promise<{
    success: boolean;
    duplicatesRemoved: number;
    groupsMerged: number;
    removedKeys: string[];
  }> {
    try {
      logger.info('Cleaning duplicate properties...');
      const result = await apiPost('/listings/dedup-clean');
      logger.info(`Removed ${result.duplicatesRemoved} duplicates across ${result.groupsMerged} groups`);
      return result;
    } catch (error: any) {
      logger.error('Error cleaning duplicates', { error: error.message });
      throw error;
    }
  },

  // ─── Property Linking ───

  // Link a property to an agent, seller, or external agent
  async linkProperty(propertyId: string, links: {
    agentId?: string;
    sellerId?: string;
    externalAgentId?: string;
    externalId?: string;
  }): Promise<Property> {
    try {
      return await apiPut<Property>(`/listings/${propertyId}/link`, links);
    } catch (error: any) {
      logger.error('Error linking property', { propertyId, links, error: error.message });
      throw error;
    }
  },

  // ─── Agent External Linking ───

  // Link an internal agent to an external Property24 agent ID
  // This enables auto-mapping of agentId on future property imports
  async linkAgentExternal(agentId: string, externalAgentId: string): Promise<{ success: boolean }> {
    try {
      return await apiPut<{ success: boolean }>(`/agents/${agentId}/link-external`, { externalAgentId });
    } catch (error: any) {
      logger.error('Error linking agent external', { agentId, externalAgentId, error: error.message });
      throw error;
    }
  },

  // Fetch all agents (for dropdowns, etc.) with graceful fallback
  async getAgents(): Promise<any[]> {
    const result = await apiGetWithFallback<any>('/agent-directory', { agents: [] });
    return Array.isArray(result) ? result : (result?.agents ?? []);
  },

  // Fetch all sellers (for dropdowns) with graceful fallback
  async getSellers(): Promise<any[]> {
    const result = await apiGetWithFallback<any>('/sellers', { sellers: [] });
    return Array.isArray(result) ? result : (result?.sellers ?? []);
  },

  // Fetch external agents from the AWS API (for dropdowns) with graceful fallback
  async getExternalAgents(): Promise<any[]> {
    return await apiGetWithFallback<any[]>('/agents', []);
  },

  // Backfill somethingPropertyId for properties synced before v2
  async backfillSpIds(): Promise<{
    success: boolean;
    backfilled: number;
    skipped: number;
    errors: number;
    total: number;
    details: any[]
  }> {
    try {
      return await apiPost('/listings/backfill-sp-ids');
    } catch (error: any) {
      logger.error('Error in backfillSpIds', { error: error.message });
      throw error;
    }
  },

  // Update listing status in KV store (call after external API status update succeeds)
  async updateLocalListingStatus(propertyId: string, listingStatus: string): Promise<{ success: boolean }> {
    try {
      return await apiPut<{ success: boolean }>(
        `/listings/${encodeURIComponent(propertyId)}/listing-status`,
        { listingStatus }
      );
    } catch (error: any) {
      logger.error('Error updating local listing status', { propertyId, listingStatus, error: error.message });
      throw error;
    }
  },
};
